import React from "react";

const SectionTitle = ({ children }) => (
  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
    {children}
  </h4>
);

const SkillTag = ({ skill }) => (
  <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700 ring-1 ring-inset ring-blue-200/50">
    {skill}
  </span>
);

const InfoRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="min-w-[80px] text-xs font-medium text-slate-500">{label}</span>
      <span className="text-slate-900">{value}</span>
    </div>
  );
};

const LinkRow = ({ label, href }) => {
  if (!href) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="min-w-[80px] text-xs font-medium text-slate-500">{label}</span>
      <a
        href={href.startsWith("http") ? href : `https://${href}`}
        target="_blank"
        rel="noopener noreferrer"
        className="truncate text-blue-600 underline-offset-2 hover:underline"
      >
        {href}
      </a>
    </div>
  );
};

export default function ResumeDetailsModal({ resume, onClose }) {
  if (!resume) return null;

  // Parse skills from various possible formats
  let skills = [];
  if (Array.isArray(resume.skills)) {
    skills = resume.skills;
  } else if (typeof resume.skills === "string") {
    skills = resume.skills.split(",").map((s) => s.trim()).filter(Boolean);
  } else if (resume.skills_json) {
    try {
      const parsed = typeof resume.skills_json === "string" ? JSON.parse(resume.skills_json) : resume.skills_json;
      skills = Array.isArray(parsed) ? parsed : [];
    } catch {
      skills = [];
    }
  }

  // Parse education
  let education = [];
  if (resume.education_json) {
    try {
      const parsed = typeof resume.education_json === "string" ? JSON.parse(resume.education_json) : resume.education_json;
      education = Array.isArray(parsed) ? parsed : [];
    } catch {
      education = [];
    }
  }

  // Parse experience
  let experience = [];
  if (resume.experience_json) {
    try {
      const parsed = typeof resume.experience_json === "string" ? JSON.parse(resume.experience_json) : resume.experience_json;
      experience = Array.isArray(parsed) ? parsed : [];
    } catch {
      experience = [];
    }
  }

  // Parse certifications
  let certifications = [];
  if (resume.certifications_json) {
    try {
      const parsed = typeof resume.certifications_json === "string" ? JSON.parse(resume.certifications_json) : resume.certifications_json;
      certifications = Array.isArray(parsed) ? parsed : [];
    } catch {
      certifications = [];
    }
  }

  // Parse extracted data
  let extractedData = {};
  if (resume.extracted_data) {
    try {
      extractedData = typeof resume.extracted_data === "string" ? JSON.parse(resume.extracted_data) : resume.extracted_data;
    } catch {
      extractedData = {};
    }
  }

  // Merge skills from extracted_data if not already found
  if (skills.length === 0 && extractedData.skills) {
    skills = Array.isArray(extractedData.skills) ? extractedData.skills : String(extractedData.skills).split(",").map((s) => s.trim()).filter(Boolean);
  }

  const candidateName = resume.extracted_name || resume.candidate_name || "Candidate";
  const candidateEmail = resume.extracted_email || resume.email || resume.contact_email || "-";
  const candidatePhone = resume.extracted_phone || resume.phone || resume.contact_phone || "-";
  const jobTitle = resume.extracted_job_title || resume.job_title || "-";
  const category = resume.extracted_category || resume.category || "-";
  const atsScore = resume.ats_score || 0;
  const experienceYears = resume.experience_years || 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{candidateName}</h3>
            <p className="mt-0.5 text-sm text-slate-500">Resume #{resume.id}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6 px-6 py-5">
          {/* Quick Info Grid */}
          <div className="grid gap-4 rounded-xl border border-gray-100 bg-gray-50 p-4 sm:grid-cols-2">
            <InfoRow label="Email" value={candidateEmail} />
            <InfoRow label="Phone" value={candidatePhone} />
            <InfoRow label="Job Title" value={jobTitle} />
            <InfoRow label="Category" value={category} />
            <InfoRow label="Experience" value={experienceYears > 0 ? `${experienceYears} years` : "N/A"} />
            <div className="flex items-center gap-2 text-sm">
              <span className="min-w-[80px] text-xs font-medium text-slate-500">ATS Score</span>
              <div className="flex items-center gap-2">
                <div className="h-2 w-20 rounded-full bg-slate-200">
                  <div
                    className={`h-2 rounded-full ${atsScore >= 80 ? "bg-emerald-500" : atsScore >= 60 ? "bg-amber-500" : "bg-rose-500"}`}
                    style={{ width: `${Math.min(100, atsScore)}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-slate-900">{atsScore}%</span>
              </div>
            </div>
            {resume.location ? (
              <InfoRow label="Location" value={resume.location} />
            ) : null}
          </div>

          {/* Skills */}
          {skills.length > 0 && (
            <div>
              <SectionTitle>Skills</SectionTitle>
              <div className="flex flex-wrap gap-1.5">
                {skills.map((skill, idx) => (
                  <SkillTag key={idx} skill={skill} />
                ))}
              </div>
            </div>
          )}

          {/* Education */}
          {education.length > 0 && (
            <div>
              <SectionTitle>Education</SectionTitle>
              <div className="space-y-2">
                {education.map((edu, idx) => (
                  <div key={idx} className="rounded-lg border border-gray-100 bg-white p-3 text-sm">
                    {edu.degree && (
                      <div className="font-medium text-slate-900">{edu.degree}</div>
                    )}
                    {edu.institution && (
                      <div className="mt-0.5 text-slate-600">{edu.institution}</div>
                    )}
                    {(edu.year || edu.duration) && (
                      <div className="mt-0.5 text-xs text-slate-400">{edu.year || edu.duration}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Experience */}
          {experience.length > 0 && (
            <div>
              <SectionTitle>Experience</SectionTitle>
              <div className="space-y-2">
                {experience.map((exp, idx) => (
                  <div key={idx} className="rounded-lg border border-gray-100 bg-white p-3 text-sm">
                    {exp.title && (
                      <div className="font-medium text-slate-900">{exp.title}</div>
                    )}
                    {exp.company && (
                      <div className="mt-0.5 text-slate-600">{exp.company}</div>
                    )}
                    {(exp.duration || exp.period) && (
                      <div className="mt-0.5 text-xs text-slate-400">{exp.duration || exp.period}</div>
                    )}
                    {exp.description && (
                      <div className="mt-1 text-xs text-slate-500">{exp.description}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Certifications */}
          {certifications.length > 0 && (
            <div>
              <SectionTitle>Certifications</SectionTitle>
              <div className="flex flex-wrap gap-1.5">
                {certifications.map((cert, idx) => {
                  const certName = typeof cert === "string" ? cert : cert.name || cert.title || "";
                  return (
                    <span
                      key={idx}
                      className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200/50"
                    >
                      {certName}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Summary */}
          {(resume.summary || extractedData.summary) && (
            <div>
              <SectionTitle>Summary</SectionTitle>
              <p className="text-sm leading-relaxed text-slate-700">
                {resume.summary || extractedData.summary}
              </p>
            </div>
          )}

          {/* Links */}
          {(resume.linkedin || resume.github || resume.portfolio) && (
            <div>
              <SectionTitle>Links</SectionTitle>
              <div className="space-y-1">
                <LinkRow label="LinkedIn" href={resume.linkedin} />
                <LinkRow label="GitHub" href={resume.github} />
                <LinkRow label="Portfolio" href={resume.portfolio} />
              </div>
            </div>
          )}

          {/* File Info */}
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
            <div className="text-xs text-slate-500">
              <span className="font-medium">File:</span> {resume.original_filename || resume.file_name || "N/A"}
              {resume.file_size ? (
                <span className="ml-3">
                  <span className="font-medium">Size:</span>{" "}
                  {resume.file_size > 1024 * 1024
                    ? `${(resume.file_size / (1024 * 1024)).toFixed(1)} MB`
                    : `${(resume.file_size / 1024).toFixed(0)} KB`}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex justify-end border-t border-gray-200 bg-white px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-slate-100 px-5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

